import { Button } from '@heidi/design-system/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@heidi/design-system/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyFootNote,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { formatRelativeTimeFromNow } from '@heidi/utils/dates';
import { useAtom, useSetAtom } from 'jotai';
import { ListChecks, MoreHorizontal, Pin, Trash2 } from 'lucide-react';
import { type MouseEvent, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { complianceNotificationDismissAtom } from './dismiss-atoms';
import { meddeviceComplianceNotificationMessages } from './messages';
import {
  type ComplianceNotificationAnalyticsPorts,
  type ComplianceNotificationType,
  type PinnedComplianceNotification,
} from './types';
import {
  complianceNotificationViewedKey,
  viewedComplianceNotificationTypesAtom,
} from './viewed-atoms';

/**
 * Compliance row — DS `Notification list inner` (Type=Recommended).
 * Figma: https://www.figma.com/design/45ZFfnZ0xnxiyEMPZYSHY4/UK-MFA---Session-Timeout?node-id=130-839
 *
 * Unread: pin icon + bold title + border, no default fill. Read: no pin, muted title.
 * Hover: surface-hover.
 *
 * Rendered in both placements — pinned above the list while unread, inline in
 * the time-ordered list once read — so the read treatment here is also what a
 * row looks like sitting among the inmails.
 */
type PinnedComplianceNotificationCardProps = {
  notification: PinnedComplianceNotification;
  /**
   * Opens what the row recommends. A port: the popover to dismiss first and the
   * analytics source belong to whoever composes the row, not to this card.
   */
  onOpenSettings: (settingsRoute: string) => void;
} & ComplianceNotificationAnalyticsPorts;

function getNotificationKeys(type: ComplianceNotificationType) {
  if (type === 'mfa_setup') {
    return {
      dismissedKey: 'mfaSetup' as const,
      readKey: 'mfaSetupRead' as const,
      firstShownKey: 'mfaSetupFirstShownAt' as const,
    };
  }
  return {
    dismissedKey: 'inactivityLogout' as const,
    readKey: 'inactivityLogoutRead' as const,
    firstShownKey: 'inactivityLogoutFirstShownAt' as const,
  };
}

export const PinnedComplianceNotificationCard = ({
  notification,
  onOpenSettings,
  onViewed,
  onClicked,
  onDismissed,
}: PinnedComplianceNotificationCardProps) => {
  const intl = useIntl();
  const setDismissState = useSetAtom(complianceNotificationDismissAtom);
  const [viewedTypes, setViewedTypes] = useAtom(viewedComplianceNotificationTypesAtom);

  const isMfa = notification.type === 'mfa_setup';
  const { readKey, dismissedKey, firstShownKey } = getNotificationKeys(notification.type);
  const titleMessage = isMfa
    ? meddeviceComplianceNotificationMessages.mfaSetupTitle
    : meddeviceComplianceNotificationMessages.inactivityLogoutTitle;
  const title = intl.formatMessage(titleMessage);
  const { isRead, firstShownAt } = notification;
  const relativeTime = formatRelativeTimeFromNow(firstShownAt, intl.locale);
  const pinned = !isRead;

  useEffect(() => {
    setDismissState((current) => {
      if (current[firstShownKey]) return current;
      return { ...current, [firstShownKey]: new Date().toISOString() };
    });
  }, [firstShownKey, setDismissState]);

  // One impression per (type, pinned) placement per session — tracked on the
  // shared `viewedComplianceNotificationTypesAtom`, not a local `useRef`. This
  // row renders inside `NotificationsList`'s virtualized `VList`, which
  // unmounts and remounts off-screen rows on scroll; a `useRef` guard resets
  // on every such remount and would re-fire an impression on every scroll
  // past the row. Keying on pinned-ness as well lets the later read placement
  // still report `is_pinned: false` after the pinned mount already fired.
  useEffect(() => {
    const key = complianceNotificationViewedKey(notification.type, pinned);
    if (viewedTypes.has(key)) return;
    setViewedTypes((current) => new Set(current).add(key));
    onViewed(notification.type, pinned);
  }, [notification.type, pinned, viewedTypes, setViewedTypes, onViewed]);

  const handleMarkReadToggle = (event: MouseEvent) => {
    event.stopPropagation();
    setDismissState((current) => ({
      ...current,
      [readKey]: !current[readKey],
    }));
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    onDismissed(notification.type);
    setDismissState((current) => ({
      ...current,
      [dismissedKey]: true,
    }));
  };

  const handleRowClick = (event: MouseEvent) => {
    event.stopPropagation();
    onClicked(notification.type);
    // Reading the row marks it read, as on every other notification. The card
    // stays pinned until the setting is actually turned on — read only demotes
    // it below unread rows and drops the bold/pin treatment.
    if (!isRead) {
      setDismissState((current) => ({ ...current, [readKey]: true }));
    }
    onOpenSettings(notification.settingsRoute);
  };

  const markReadLabel = isRead ? (
    <FormattedMessage defaultMessage="Mark as unread" id="vLAPJDu9y6" />
  ) : (
    <FormattedMessage defaultMessage="Mark as read" id="64Hq/+q+KI" />
  );
  const deleteLabel = <FormattedMessage defaultMessage="Delete" id="K3r6DQW7h+" />;

  return (
    <ContextMenu>
      {/* Row is a `ContextMenuTrigger`, not a `role="button"` div, for the same
          reason the sibling `NotificationCard` is: a button row may not contain
          the kebab button, and screen readers drop nested controls. Keyboard
          users reach both actions through the kebab, which is focusable. */}
      <ContextMenuTrigger
        aria-label={title}
        onClick={handleRowClick}
        className={cn(
          'group mb-0.5 flex w-full cursor-pointer flex-col gap-0.5 rounded-lg px-3.5 pb-2 pt-3',
          'transition ease-in-out hover:bg-surface-hover',
          !isRead && 'border border-border'
        )}
        data-testid={`pinned-compliance-notification-${notification.type}`}
      >
        <div className="flex min-w-0 items-start gap-1">
          {!isRead ? (
            <Pin aria-hidden="true" className="size-4 shrink-0 text-text-primary" />
          ) : null}
          {isRead ? (
            <TypographyCaption className="text-text-secondary">{title}</TypographyCaption>
          ) : (
            <TypographyCaptionBold>{title}</TypographyCaptionBold>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          {relativeTime ? (
            <TypographyFootNote className="text-text-secondary">
              {relativeTime}
            </TypographyFootNote>
          ) : (
            <span />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="panel_ghost"
                size="xxs"
                iconOnly
                flush="end"
                aria-label={intl.formatMessage({
                  defaultMessage: 'Notification actions',
                  id: 'fVQD3wh+RY',
                })}
                onClick={(event) => event.stopPropagation()}
                className={cn(
                  'shrink-0 transition-opacity duration-200 ease-out',
                  'pointer-fine:opacity-0',
                  'pointer-fine:group-hover:opacity-100',
                  'pointer-fine:focus-visible:opacity-100',
                  'pointer-fine:data-[state=open]:opacity-100'
                )}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-48"
              side="bottom"
              align="end"
              alignOffset={-12.5}
            >
              <DropdownMenuItem
                className="flex !cursor-pointer justify-start gap-2"
                onClick={handleMarkReadToggle}
              >
                <ListChecks size={14} />
                <TypographyP2>{markReadLabel}</TypographyP2>
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                className="flex !cursor-pointer justify-start gap-2"
                onClick={handleDelete}
              >
                <Trash2 size={14} />
                <TypographyP2>{deleteLabel}</TypographyP2>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="w-48">
        <ContextMenuItem onClick={handleMarkReadToggle}>
          <ListChecks />
          <TypographyP2>{markReadLabel}</TypographyP2>
        </ContextMenuItem>
        <ContextMenuItem variant="destructive" onClick={handleDelete}>
          <Trash2 />
          <TypographyP2>{deleteLabel}</TypographyP2>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};
